import { observer } from "mobx-react";
import {
  Button,
  Checkbox,
  HStack,
  Stack,
  Text,
  Box,
  RadioGroup,
  Alert,
  NativeSelect,
  Field,
  Input,
  Spacer,
  Progress,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import mapboxgl from "mapbox-gl";
import { mapActive, mapUtils } from "../../../../../../map/utils/map-utils";
import { Survey } from "../../../../../../survey/models/Survey";
import { SurveyApi } from "../../../../../../survey/services/SurveyApi";
import { OptionalScope } from "../../../../../../shared/models/OptionalScope";
import { Feature, FeatureCollection, Point, Position } from "geojson";
import { copyNodesMapSource } from "../../../../../../map/sources/copyNodesMapSource";
import { CraNode } from "../../../../../../cra/models/CraNode";
import { GeoJsonNodeProps } from "../../../../../../cra/models/CustomGeoJsonProperties";
import { CraWayApi } from "../../../../../../cra/services/CraWayApi";
import { CraNodeApi } from "../../../../../../cra/services/CraNodeApi";
import { useStores } from "../../../../../../shared/stores/RootStore";
import { toast } from "../../../../../../shared/views/CustomToaster";
import { CustomDialog } from "../../../../../../shared/views/CustomDialog";
import { compileAllPages } from "../../../../../../shared/utils/http-utils";
import { JobStateApi } from "../../../../../../shared/services/JobStateApi";
import { ProgressState } from "../../../../../../shared/models/JobState";
import { CraWay } from "../../../../../../cra/models/CraWay";

const i18prefix = "map:bottomBar.copyNodesModal";
const mapId = "copyNodesMap";

enum AppendType {
  Start = "start",
  End = "end",
  Custom = "custom",
}

enum SubmissionStep {
  Copying = "copying",
  Deleting = "deleting",
}

export const CopyNodesModal: React.FC<{
  onClose: () => void;
}> = observer(({ onClose }) => {
  const { surveyStore, craWayStore, craNodeStore, codingStore } = useStores();

  const [map, setMap] = useState<mapboxgl.Map>();
  const [keepOriginal, setKeepOriginal] = useState(true);
  const [appendType, setAppendType] = useState(AppendType.Start);
  const [appendIndex, setAppendIndex] = useState(0);
  const [submissionStep, setSubmissionStep] = useState<SubmissionStep>();
  const [submissionProgress, setSubmissionProgress] = useState<ProgressState>();

  const [surveys, setSurveys] = useState<Survey[]>();
  const [selectedSurvey, setSelectedSurvey] = useState<Survey>();

  const [craWays, setCraWays] = useState<CraWay[]>();
  const [selectedCraWay, setSelectedCraWay] = useState<CraWay>();

  const originWay = craWayStore.getSelected()!;
  const selectedNodes = craNodeStore.getMultiSelectedNodesByCraWay(
    originWay.id,
  );

  // use this to avoid crashes when selecting a different route
  const resetAppendState = () => {
    setAppendType(AppendType.Start);
    setAppendIndex(0);
  };

  const targetIsEmptyOrNull = () =>
    !selectedCraWay?.geoJson ||
    (selectedCraWay?.geoJson && !selectedCraWay.geoJson.features.length);

  const getSortedSelectedNodes = () =>
    selectedNodes.sort((a, b) => a.index - b.index);

  const geoJson = useMemo<FeatureCollection>(() => {
    const buildConnectionLineFeature = (
      from: Position,
      to: Position,
    ): Feature => ({
      type: "Feature",
      properties: {
        stroke: "#00a498",
        line: "#ffffff",
      },
      geometry: {
        type: "LineString",
        coordinates: [from, to],
      },
    });

    const getConnectionLines = (
      targetFeatures: Feature[],
      nodes: CraNode[],
    ): Feature[] => {
      const targetPoints = targetFeatures
        .map((feat) => feat.geometry)
        .filter((geometry) => geometry.type === "Point");

      const lineFeatures: Feature[] = [];

      if (appendIndex > 0) {
        // add line that connects the first node to the previous feature
        const previousPoint = targetPoints[appendIndex - 1];
        const from: Position = previousPoint.coordinates;
        const firstNode = nodes[0];
        const to: Position = [firstNode.longitude, firstNode.latitude];
        lineFeatures.push(buildConnectionLineFeature(from, to));
      }

      if (appendIndex < targetPoints.length) {
        // add line that connects the last node to the next feature
        const lastNode = nodes[nodes.length - 1];
        const from: Position = [lastNode.longitude, lastNode.latitude];
        const nextPoint = targetPoints[appendIndex];
        const to: Position = nextPoint.coordinates;
        lineFeatures.push(buildConnectionLineFeature(from, to));
      }

      return lineFeatures;
    };

    var features: Feature[] = [];

    const sortedNodes = getSortedSelectedNodes();

    if (selectedCraWay?.geoJson) {
      // add target data collection
      features.push({
        type: "Feature",

        properties: {
          stroke: "#000000",
          line: "#ffffff",
        },
        geometry: {
          type: "LineString",
          coordinates: selectedCraWay.geoJson.features
            .filter((feature) => feature.geometry.type === "Point")
            .map((feature) => feature.geometry as Point)
            .map((lineString) => lineString.coordinates),
        },
      });

      // add connection between copied nodes and the target data collection
      const targetPointFeatures = selectedCraWay.geoJson.features.filter(
        (feat) => feat.geometry.type === "Point",
      );
      const connectionLines = getConnectionLines(
        targetPointFeatures,
        sortedNodes,
      );

      features.push(...connectionLines);
    }

    // add copied nodes
    sortedNodes.forEach((node, index, list) => {
      const isLastItem = index + 1 === list.length;

      if (!isLastItem) {
        const nextNode = list[index + 1];
        features.push({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [node.longitude, node.latitude],
              [nextNode.longitude, nextNode.latitude],
            ],
          },
          properties: {
            stroke: "#00a498",
            line: "#ffffff",
          },
        });
      }
    });

    return {
      type: "FeatureCollection",
      features,
    };

    // eslint-disable-next-line
  }, [selectedCraWay, appendType, appendIndex, map]);

  useEffect(() => {
    const newMap = new mapboxgl.Map({
      ...mapUtils.defaultConfig,
      container: mapId,
    });
    newMap.on("load", () => {
      newMap.resize();
      copyNodesMapSource.config(newMap);

      newMap.addControl(new mapboxgl.NavigationControl());
      newMap.addControl(
        new mapboxgl.ScaleControl({
          maxWidth: 100,
          unit: "metric",
        }),
      );
      setMap(newMap);
    });

    return () => {
      newMap.remove();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mapActive(map)) {
      mapUtils.centerMap(map!, (bounds) => {
        copyNodesMapSource.extendBounds(bounds, geoJson);
      });
    }
    // eslint-disable-next-line
  }, [selectedCraWay, map]);

  useEffect(() => {
    if (mapActive(map)) {
      copyNodesMapSource.update(map!, geoJson);
    }
    // eslint-disable-next-line
  }, [geoJson]);

  useEffect(() => {
    const preSelectedSurvey = surveyStore.getSelected();
    if (preSelectedSurvey) setSelectedSurvey(preSelectedSurvey);
    setSurveys(surveyStore.surveys);
    setSelectedCraWay(originWay);
    resetAppendState();

    compileAllPages(
      async (pageNumber) => await SurveyApi.page(20, pageNumber),
    ).then((surveys) => {
      setSurveys(surveys);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedSurvey) {
      SurveyApi.pageCraWays(
        selectedSurvey.id,
        [OptionalScope.GeoJson, OptionalScope.Stats],
        200,
        0,
      ).then(({ data: page }) => {
        setCraWays(page.data);
        if (page.data.length === 1) {
          setSelectedCraWay(page.data[0]);
          resetAppendState();
        }
      });
    }
    // eslint-disable-next-line
  }, [selectedSurvey]);

  useEffect(() => {
    if (
      selectedCraWay &&
      appendType === AppendType.End &&
      targetIsEmptyOrNull()
    ) {
      setAppendType(AppendType.Start);
    }
    // eslint-disable-next-line
  }, [selectedCraWay]);

  const onSubmit = async () => {
    setSubmissionStep(SubmissionStep.Copying);
    const targetCraWay = selectedCraWay;

    const sortedNodes = getSortedSelectedNodes();

    var pasteAtIndex = 0;
    const targetGeoJson = targetCraWay!.geoJson;
    if (appendIndex > 0 && targetGeoJson?.features.length) {
      const firstDisplacedFeature =
        targetGeoJson.features[(appendIndex - 1) * 2];
      const { id } = firstDisplacedFeature.properties as GeoJsonNodeProps;

      const { data: firstDisplacedNode } = await CraNodeApi.get(id, []);

      pasteAtIndex = firstDisplacedNode.index + 1;
    }

    const onCopySuccess = async () => {
      if (!keepOriginal) {
        setSubmissionStep(SubmissionStep.Deleting);
        const totalNodes = craNodeStore.multiSelectedIds.size;
        var deletedNodes = 0;

        await craNodeStore.deleteSelectedNodes(() => {
          deletedNodes++;
          setSubmissionProgress({ value: deletedNodes, total: totalNodes });
        });
        await craWayStore.compileCraWayNodes(originWay.id);
      }
      const targetCraWayNeedsRefresh = craWayStore.ways.find(
        (w) => w.id === targetCraWay!.id,
      );
      if (targetCraWayNeedsRefresh) {
        await craWayStore.refreshCraWay(targetCraWay!.id);
        await craNodeStore.fetchNodes(targetCraWay!.id);
        await codingStore.fetchAttributesByCraWayId(targetCraWay!.id);
      }
      toast("success", t(`${i18prefix}.toast.success`));

      onClose();
    };

    await CraWayApi.copyNodes(originWay.id, {
      targetCraWayId: targetCraWay!.id,
      pasteAtIndex,
      indexes: sortedNodes.map((node) => node.index),
    })
      .then(async ({ data: jobStateId }) => {
        await JobStateApi.poll(jobStateId, 1000, (jobState) => {
          setSubmissionProgress({
            value: jobState.progressValue,
            total: jobState.progressTotal,
          });
        });

        await onCopySuccess();
      })
      .catch(() => {
        toast("error", t(`${i18prefix}.toast.error`));
      })
      .finally(() => {
        setSubmissionStep(undefined);
      });
  };

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={t(`${i18prefix}.title`)}
      footer={
        <HStack flexGrow={1}>
          {!!submissionStep && (
            <ProgressBar
              submissionProgress={submissionProgress}
              submissionStep={submissionStep}
            />
          )}

          <Spacer />
          <Button
            colorPalette={"primary"}
            loading={!!submissionStep}
            onClick={onSubmit}
            disabled={!selectedCraWay}
          >
            {t(`${i18prefix}.submit`)}
          </Button>
        </HStack>
      }
    >
      <HStack alignItems="stretch" gap={6}>
        <Stack width={"340px"} gap={6}>
          <Stack>
            <Text fontSize={"xs"} textTransform="uppercase">
              {t(`${i18prefix}.destination`)}
            </Text>

            <NativeSelect.Root disabled={!surveys}>
              <NativeSelect.Field
                placeholder={t(`${i18prefix}.selectSurvey`)}
                value={selectedSurvey?.id ?? ""}
                onChange={(e) => {
                  const surveyId = e.target.value;
                  const survey = surveys?.find((p) => p.id === surveyId);
                  setSelectedSurvey(survey);
                }}
              >
                {surveys?.map((survey) => (
                  <option key={survey.id} value={survey.id}>
                    {survey.city}: {survey.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            <NativeSelect.Root disabled={!craWays}>
              <NativeSelect.Field
                placeholder={t(`${i18prefix}.selectRoute`)}
                value={selectedCraWay?.id ?? ""}
                onChange={(e) => {
                  const craWayId = e.target.value;
                  const craWay = craWays?.find((c) => c.id === craWayId);
                  setSelectedCraWay(craWay);
                  resetAppendState();
                }}
              >
                {craWays?.map((way) => (
                  <option key={way.id} value={way.id}>
                    {way.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            {!!selectedCraWay && (
              <Alert.Root
                overflow="hidden"
                status={
                  selectedCraWay?.permissions.canCreate ? "info" : "error"
                }
                py={2}
                px={3}
              >
                <Alert.Indicator />
                <Alert.Title fontWeight={"normal"} fontSize="sm">
                  {selectedCraWay?.permissions.canCreate
                    ? selectedCraWay.stats?.nNodes
                      ? selectedCraWay.id === originWay.id
                        ? t(`${i18prefix}.alert.sameCraWay`)
                        : t(`${i18prefix}.alert.hasNodes`)
                      : t(`${i18prefix}.alert.isEmpty`)
                    : t(`${i18prefix}.alert.noPermissions`)}
                </Alert.Title>
              </Alert.Root>
            )}
          </Stack>

          <Checkbox.Root
            colorPalette="primary"
            checked={keepOriginal}
            onCheckedChange={({ checked }) => {
              setKeepOriginal(!!checked);
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>{t(`${i18prefix}.keepOriginal`)}</Checkbox.Label>
          </Checkbox.Root>

          <Stack>
            <Text>{t(`${i18prefix}.appendTo`)}</Text>

            <RadioGroup.Root
              colorPalette="primary"
              value={appendType}
              onValueChange={({ value }) => {
                const appendType = value as AppendType;
                setAppendType(appendType);
                if (appendType === AppendType.Start) {
                  setAppendIndex(0);
                } else if (appendType === AppendType.End) {
                  setAppendIndex(selectedCraWay!.stats!.nNodes);
                }
              }}
            >
              <HStack gap={6}>
                <AppendTypeRadioItem appendType={AppendType.Start} />
                <AppendTypeRadioItem
                  appendType={AppendType.End}
                  disabled={targetIsEmptyOrNull()}
                />
                <AppendTypeRadioItem
                  appendType={AppendType.Custom}
                  disabled={targetIsEmptyOrNull()}
                />
              </HStack>
            </RadioGroup.Root>

            {appendType === AppendType.Custom && (
              <Field.Root>
                <Input
                  type="number"
                  value={appendIndex}
                  onChange={(e) => {
                    const intValue = parseInt(e.target.value || "0");

                    if (
                      selectedCraWay &&
                      intValue >= 0 &&
                      intValue <= selectedCraWay!.stats!.nNodes
                    ) {
                      setAppendIndex(intValue);
                    }
                  }}
                ></Input>
              </Field.Root>
            )}
          </Stack>
        </Stack>

        <Box id={mapId} flexGrow={1} height="460px" />
      </HStack>
    </CustomDialog>
  );
});

const AppendTypeRadioItem = ({
  appendType,
  disabled = false,
}: {
  appendType: AppendType;
  disabled?: boolean;
}) => {
  return (
    <RadioGroup.Item value={appendType} disabled={disabled}>
      <RadioGroup.ItemHiddenInput />
      <RadioGroup.ItemIndicator />
      <RadioGroup.ItemText>
        {t(`${i18prefix}.appendType.${appendType}`)}
      </RadioGroup.ItemText>
    </RadioGroup.Item>
  );
};

export default AppendTypeRadioItem;

const ProgressBar = ({
  submissionStep,
  submissionProgress,
}: {
  submissionStep: SubmissionStep;
  submissionProgress: ProgressState | undefined;
}) => {
  return (
    <Progress.Root
      width={"200px"}
      size={"md"}
      value={submissionProgress?.value ?? null}
      max={submissionProgress?.total}
      striped
      animated
      colorPalette={
        submissionStep === SubmissionStep.Copying ? "primary" : "red"
      }
    >
      <Progress.Track>
        <Progress.Range />
      </Progress.Track>
      {!!submissionProgress && (
        <Progress.ValueText>
          {t(`${i18prefix}.progress.${submissionStep}`)}
        </Progress.ValueText>
      )}
    </Progress.Root>
  );
};
